/**
 * CustomRoundModal Component
 * 
 * Full-screen modal for creating and editing custom interview rounds
 * 
 * Architecture:
 * - Main orchestrator that manages state and coordinates sub-components
 * - Uses custom hooks for business logic
 * - Delegates UI to sub-components for each column
 * 
 * Layout:
 * - Column 1: AI Template Generator (25%)
 * - Column 2: Round Form (47%)
 * - Column 3: Sample Questions (28%)
 */

import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Form,
  message,
} from "antd";
import { useFormContext } from "react-hook-form";
import interviewService from "../../../../../../Service/interview.service";
import { InterviewRound } from "../../../../../../Dto/GenerateJobDescription.dto";
import { InterviewRoundState } from "../../utils/formTransformers";
import { Requirements } from "../../../../../../Entities/JobDescription";
import { JobPostFormData } from "../../../../schemas/jobPostValidation";
import {
  logError,
  getUserFriendlyErrorMessage,
  withRetry,
} from "../../../../utils/errorHandling";
import { InterviewRoundsDTO } from "../../../../../../Entities/InterviewRounds";
import { DEFAULT_PASS_PERCENTAGE, MIN_PASS_PERCENTAGE, MAX_PASS_PERCENTAGE } from "../../../../utils/constants";

// Local imports
import { CustomRoundModalProps, RoundFormValues } from "./types";
import { MODAL_CONFIG, SHARED_STYLES } from "./constants";
import { useTemplateGenerator, useSampleQuestionsHook } from "./hooks";
import { 
  AITemplateGenerator, 
  RoundForm, 
  SampleQuestionsPanel 
} from "./components";

const CustomRoundModal: React.FC<CustomRoundModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingRound,
  existingRounds = [],
  initialPassPercentage,
  initialIsMustQualify,
  settingsOnlyMode = false,
}) => {
  const [form] = Form.useForm<RoundFormValues>();
  const { watch: watchFormContext } = useFormContext<JobPostFormData>();
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InterviewRound | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const isEditing = !!editingRound;

  // Watch form values
  const formValues = Form.useWatch([], form);

  // Get data from form context
  const step0Data = watchFormContext("step0");
  const step2Data = watchFormContext("step2");

  // Extract requirements from Step 2
  const requirements: Requirements[] = useMemo(() => {
    if (!step2Data || !step2Data.requirements) {
      return [];
    }
    return step2Data.requirements.map((req: any) => ({
      id: req.id || 0,
      topic: req.topic || "",
      description: req.description || "",
      mustMatch: req.mustMatch ?? true,
    }));
  }, [step2Data]);

  // Available skills from job description (pre-filled from Step 0)
  const availableSkills: string[] = useMemo(() => {
    return step0Data?.skills || [];
  }, [step0Data]);

  // Sample Questions Hook
  const sampleQuestionsState = useSampleQuestionsHook({
    form,
    formValues,
    requirements,
    editingRound,
  });

  // Template Generator Hook
  const templateGenerator = useTemplateGenerator({
    form,
    requirements,
    formValues,
    onSampleQuestionsReset: sampleQuestionsState.resetSampleQuestions,
  });

  // Check if question type is simulation
  const isSimulationRound = formValues?.questionType === "simulation";

  // Check if form fields are filled and valid for submission
  const isFormFilled = useMemo(() => {
    const passPercentage = formValues?.passPercentage;
    const isPassValid = typeof passPercentage === 'number' &&
      passPercentage >= MIN_PASS_PERCENTAGE &&
      passPercentage <= MAX_PASS_PERCENTAGE;

    if (settingsOnlyMode) {
      return isPassValid;
    }

    const title = formValues?.title?.trim();

    // Simulation rounds only need a title (no prompt/evaluation)
    if (isSimulationRound) {
      return !!(title && isPassValid);
    }

    const questionGenerationPrompt = formValues?.questionGenerationPrompt?.trim();
    return !!(title && questionGenerationPrompt && isPassValid);
  }, [formValues, settingsOnlyMode, isSimulationRound]);

  // Watch question type to conditionally show/hide Evaluation Criteria
  const questionType = Form.useWatch("questionType", form);
  const shouldShowEvaluationCriteria = Boolean(questionType && questionType !== "mcq");

  // Track form dirty state - check if user has made meaningful changes
  useEffect(() => {
    if (!visible) {
      setIsDirty(false);
      return;
    }
    
    // Check if form has meaningful content beyond defaults
    const title = formValues?.title?.trim() || "";
    const prompt = formValues?.questionGenerationPrompt?.trim() || "";
    
    // In edit mode, compare against original values
    if (isEditing && editingRound) {
      const originalTitle = editingRound.title?.trim() || "";
      const originalPrompt = editingRound.questionGenerationPrompt?.trim() || "";
      const hasChanges = title !== originalTitle || prompt !== originalPrompt;
      setIsDirty(hasChanges);
    } else {
      // In create mode, check if user has entered content
      setIsDirty(title.length > 0 || prompt.length > 0);
    }
  }, [visible, formValues?.title, formValues?.questionGenerationPrompt, isEditing, editingRound]);

  // Handle cancel with confirmation if dirty
  const handleCancel = () => {
    if (isDirty && !loading) {
      Modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Discard",
        cancelText: "Keep Editing",
        okButtonProps: { danger: true },
        onOk: () => {
          setIsDirty(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (visible) {
      if (!editingRound) {
        // Reset form for new round
        form.resetFields();
        setSelectedTemplate(null);
        form.setFieldsValue({
          questionType: "mixed",
          passPercentage: DEFAULT_PASS_PERCENTAGE,
          isMustQualify: true,
          evaluationCriteria: "", // Initialize to ensure field is registered
        });
        
        // Initialize template generator with skills from job description
        templateGenerator.initializeFromJobDescription(availableSkills);
      } else {
        // Initialize template generator for editing mode
        templateGenerator.initializeFromEditingRound(editingRound.answerType, availableSkills);
      }
    } else {
      // Cleanup on close - cancel any pending requests
      templateGenerator.cancelGeneration();
      form.resetFields();
      setSelectedTemplate(null);
      setIsDirty(false);
      sampleQuestionsState.resetSampleQuestions();
      templateGenerator.resetState();
    }
  }, [visible, availableSkills, editingRound]);

  // Handle editing round initialization
  useEffect(() => {
    if (!visible || !editingRound) {
      return;
    }

    const isOverallAssessment =
      editingRound.title?.trim().toLowerCase() === "overall assessment";

    // Normalize question type
    const rawAnswerType = editingRound.answerType || (isOverallAssessment ? "mixed" : "mcq");
    const questionTypeValue = typeof rawAnswerType === "string"
      ? rawAnswerType.trim().toLowerCase()
      : "mcq";

    const validQuestionTypes = ["mcq", "descriptive", "coding", "mixed", "simulation"];
    const normalizedQuestionType = validQuestionTypes.includes(questionTypeValue)
      ? questionTypeValue
      : "mcq";

    const formData = {
      title: editingRound.title || "",
      questionType: normalizedQuestionType,
      questionGenerationPrompt:
        editingRound.questionGenerationPrompt ||
        (isOverallAssessment ? editingRound.description || "" : ""),
      evaluationCriteria: editingRound.evaluationPrompt || "",
      passPercentage: initialPassPercentage ?? DEFAULT_PASS_PERCENTAGE,
      isMustQualify: initialIsMustQualify ?? true,
    };

    // Set form values after a brief delay to ensure form is ready
    const timer = setTimeout(() => {
      form.setFieldsValue(formData);
      Promise.resolve().then(() => {
        form.setFieldValue("questionType", normalizedQuestionType);
      });
    }, 10);

    return () => clearTimeout(timer);
  }, [visible, editingRound, form, initialPassPercentage, initialIsMustQualify]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values: RoundFormValues = await form.validateFields();

      // In settings-only mode, just return the updated settings
      if (settingsOnlyMode && editingRound) {
        message.success("Round settings updated");
        setIsDirty(false);
        onSubmit(editingRound, values.passPercentage, values.isMustQualify);
        return;
      }

      // Check for duplicates for new rounds with selected template
      if (!isEditing && selectedTemplate && selectedTemplate._id) {
        const isDuplicate = existingRounds.some((round) => {
          const isProfileMatching =
            round.title?.toLowerCase() === "profile matching" ||
            round.interviewRoundId === "profile-match" ||
            round._id === "profile-match";
          const isOverallAssessment =
            round.title?.toLowerCase() === "overall assessment" ||
            round.interviewRoundId === "overall-assessment" ||
            round._id === "overall-assessment";

          if (isProfileMatching || isOverallAssessment) {
            return false;
          }

          const roundId = round.interviewRoundId || round._id;
          return roundId && roundId === selectedTemplate._id;
        });

        if (isDuplicate) {
          message.error(
            "This assessment has already been added. Please select a different assessment template."
          );
          return;
        }

        // Check if template was modified
        const isTemplateModified =
          values.title?.trim() !== selectedTemplate.title?.trim() ||
          values.questionType !== selectedTemplate.answerType ||
          values.questionGenerationPrompt?.trim() !== selectedTemplate.questionGenerationPrompt?.trim() ||
          values.evaluationCriteria?.trim() !== selectedTemplate.evaluationPrompt?.trim();

        // If template is used as-is, just use the template directly
        if (!isTemplateModified) {
          message.success("Assessment added successfully");
          setIsDirty(false);
          onSubmit(selectedTemplate, values.passPercentage, values.isMustQualify);
          return;
        }
      }

      setLoading(true);

      if (isEditing && editingRound?._id) {
        // Update existing round
        const roundData = {
          roundName: values.title,
          customRound: {
            _id: editingRound._id,
            question_type: values.questionType,
            custom_ai_prompt: values.questionGenerationPrompt,
            evaluation_criteria: values.evaluationCriteria || "",
            question_id: 2,
          },
          roundNumber: "1",
          category: ["Custom"],
        };

        const updatePayload: InterviewRoundsDTO = {
          _id: editingRound._id,
          ...roundData,
        };

        const updatedRound = await withRetry(
          () => interviewService.updateInterviewRound(updatePayload),
          3,
          1000,
          (error) => {
            const status = error?.response?.status;
            return !status || status >= 500;
          }
        );
        message.success("Assessment updated successfully");
        setIsDirty(false);
        onSubmit(updatedRound, values.passPercentage, values.isMustQualify);
      } else {
        // Create new round
        const roundData = {
          roundName: values.title,
          customRound: {
            _id: "",
            question_type: values.questionType,
            custom_ai_prompt: values.questionGenerationPrompt,
            evaluation_criteria: values.evaluationCriteria || "",
            question_id: 2,
          },
          roundNumber: "1",
          category: ["Custom"],
        };

        const createdRound = await withRetry(
          () => interviewService.createInterviewRound(roundData),
          3,
          1000,
          (error) => {
            const status = error?.response?.status;
            return !status || status >= 500;
          }
        );
        message.success("Round created successfully");
        setIsDirty(false);
        onSubmit(createdRound, values.passPercentage, values.isMustQualify);
      }
    } catch (error: any) {
      const errorFormValues = form.getFieldsValue();
      logError("CustomRoundModal.handleSubmit", error, {
        isEditing,
        editingRoundId: editingRound?._id,
        roundTitle: errorFormValues.title,
        questionType: errorFormValues.questionType,
      });

      const errorMessage = getUserFriendlyErrorMessage(
        error,
        isEditing
          ? "Failed to update assessment. Please check your input and try again."
          : "Failed to create assessment. Please check your input and try again."
      );
      message.error(errorMessage, 5);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      title={isEditing ? "Edit Interview Round" : "Create Interview Round"}
      width={MODAL_CONFIG.width}
      centered
      style={{ 
        maxWidth: MODAL_CONFIG.maxWidth,
      }}
      styles={{
        body: {
          height: "calc(90vh - 55px)", // Account for header only (~55px), footer is inside columns
          maxHeight: "calc(90vh - 55px)",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      footer={null}
      destroyOnClose
    >
      {/* Three Column Layout */}
      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Column 1: AI Template Generator - Hidden for simulation rounds */}
        {!isSimulationRound && (
        <AITemplateGenerator
          availableSkills={availableSkills}
          selectedSkills={templateGenerator.selectedSkills}
          onSkillToggle={templateGenerator.handleSkillToggle}
          onSelectAllSkills={() => templateGenerator.handleSelectAllSkills(availableSkills)}
          onClearAllSkills={templateGenerator.handleClearAllSkills}
          customSkillInput={templateGenerator.customSkillInput}
          onCustomSkillInputChange={templateGenerator.setCustomSkillInput}
          onAddCustomSkill={() => templateGenerator.handleAddCustomSkill(availableSkills)}
          selectedQuestionType={templateGenerator.selectedQuestionType}
          onQuestionTypeChange={templateGenerator.handleQuestionTypeChange}
          questionStyles={templateGenerator.questionStyles}
          filteredQuestionStyles={templateGenerator.filteredQuestionStyles}
          selectedQuestionStyle={templateGenerator.selectedQuestionStyle}
          onQuestionStyleChange={templateGenerator.handleQuestionStyleChange}
          selectedDifficulty={templateGenerator.selectedDifficulty}
          onDifficultyChange={templateGenerator.handleDifficultyChange}
          isLoadingStyles={templateGenerator.isLoadingStyles}
          isGeneratingTemplate={templateGenerator.isGeneratingTemplate}
          isTemplateGeneratorReady={templateGenerator.isTemplateGeneratorReady}
          onGenerateTemplate={templateGenerator.handleGenerateTemplate}
          generationStep={templateGenerator.generationStep}
          generationSteps={templateGenerator.generationSteps}
          settingsOnlyMode={settingsOnlyMode}
        />
        )}

        {/* Column 2: Round Form */}
        <RoundForm
          form={form}
          formValues={formValues}
          settingsOnlyMode={settingsOnlyMode}
          shouldShowEvaluationCriteria={shouldShowEvaluationCriteria}
          isFormFilled={isFormFilled}
          isEditing={isEditing}
          isGeneratingTemplate={templateGenerator.isGeneratingTemplate}
          onQuestionTypeChange={templateGenerator.handleQuestionTypeChangeFromForm}
          hasRequirements={requirements.length > 0}
          hasSampleQuestions={sampleQuestionsState.sampleQuestions.length > 0}
          isSampleQuestionsLoading={sampleQuestionsState.isLoadingQuestions}
          onGenerateSampleQuestions={sampleQuestionsState.fetchSampleQuestions}
        />

        {/* Column 3: Sample Questions Panel */}
        <SampleQuestionsPanel
          isLoading={sampleQuestionsState.isLoadingQuestions}
          questions={sampleQuestionsState.sampleQuestions}
          hasFormChangedAfterGeneration={sampleQuestionsState.hasFormChangedAfterGeneration}
          isFormFilled={isFormFilled}
          isEditing={isEditing}
          isSubmitting={loading}
          settingsOnlyMode={settingsOnlyMode}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          isSimulationRound={isSimulationRound}
        />
      </div>
    </Modal>
  );
};

export default CustomRoundModal;
